@if (closed && exitOnCompletion) {
<div>
    Booking complete, you can now close this window.
</div>
}
@if (!closed && unsuitable) {
<div class="unsuitable-container">
    @if (floatingBookingDisabled) {
    <div class="disabled-message">
        <mat-icon color="warn">lock</mat-icon>
        <div>
            <h3>Floating Booking is Disabled</h3>
            <p>Floating booking is currently disabled in the system. Please use standard booking or contact your
                administrator.</p>
            <button mat-raised-button color="primary" (click)="switchToStandardBooking()">
                <mat-icon>view_week</mat-icon> Switch to Standard Booking
            </button>
        </div>
    </div>
    } @else {
    <div>This visit is not at a status suitable for booking.</div>
    }
</div>
}
@if (!closed && !unsuitable) {
<div>
    <ngx-ui-loader [loaderId]="'bookingLoader'" [text]="currentLoadingMessage"></ngx-ui-loader>
    <mat-toolbar color="primary">
        @if (!exitOnCompletion) {
        <button (click)="returnToMenu()" mat-icon-button>
            <mat-icon>arrow_back</mat-icon>
        </button>
        }
        <div class="toolbar-content">
            <h2>Floating Booking</h2>
        </div>
        <div class="toolbar-actions">
            <button (click)="openReferralForm()" color="warn" mat-flat-button>Refer to Planning Team</button>
            <button (click)="switchToStandardBooking()" color="accent" mat-flat-button>
                <mat-icon>view_week</mat-icon> Switch to Standard Booking
            </button>
        </div>
    </mat-toolbar>
    <div class="booking-container">
        <div class="booking-sidebar">
            @if (visit) {
            <mat-list>
                <h3 mat-subheader>Job Details</h3>
                <mat-list-item class="job-ref-item">
                    <mat-icon fontIcon="fa-info-circle" fontSet="far" mat-list-icon></mat-icon>
                    <h4 mat-line>{{ visit.job.externalJobReference }}</h4>
                    <p mat-line>Job Reference</p>
                </mat-list-item>

                <div class="priority-container">
                    <div [style.background-color]="visit.job.priority.backgroundColor"
                        [style.color]="visit.job.priority.foregroundColor" class="priority-badge">
                        <span class="priority-title">{{ visit.job.priority.description }}</span>
                        <span class="priority-due">Due {{ visit.job.planningWindowEnd | amDateFormat: 'DD/MM/YYYY HH:mm'
                            }}</span>
                    </div>
                </div>

                @if (isOverdue) {
                <mat-list-item class="overdue-item">
                    <mat-icon color="warn" fontIcon="fa-exclamation-triangle" fontSet="fas" mat-list-icon></mat-icon>
                    <h4 mat-line>{{ visit.job.planningWindowEnd | amTimeAgo }} Overdue</h4>
                </mat-list-item>
                }
                <mat-list-item class="property-item">
                    <mat-icon fontIcon="fa-house" fontSet="far" mat-list-icon></mat-icon>
                    <h4 mat-line>{{ visit.job.externalPropertyReference }}</h4>
                    <p mat-line>Property Reference</p>
                </mat-list-item>
            </mat-list>
            }
            <mat-divider></mat-divider>
            @if (visit && visit.job) {
            <app-visit-property-list-component
                [propertyId]="visit.job.externalPropertyReference"></app-visit-property-list-component>
            }
        </div>
        <div class="booking-main">
            <div class="floating-booking-header">
                <h3>Select Date Range for Floating Booking</h3>
                <p>This visit is unappointed and will automatically move to the best time within the date range
                    specified.</p>
            </div>

            <form [formGroup]="dateRange" class="date-range-form">
                <div class="date-inputs-row">
                    <div class="date-inputs">
                        <mat-form-field appearance="outline">
                            <mat-label>From Date</mat-label>
                            <input matInput [matDatepicker]="startPicker" formControlName="startDate"
                                placeholder="Select start date" aria-label="Start date">
                            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                            <mat-datepicker #startPicker></mat-datepicker>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>To Date</mat-label>
                            <input matInput [matDatepicker]="endPicker" formControlName="endDate"
                                placeholder="Select end date" aria-label="End date">
                            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                            <mat-datepicker #endPicker></mat-datepicker>
                        </mat-form-field>
                    </div>

                    <button [disabled]="!dateRange.valid" (click)="checkAvailability()" mat-raised-button
                        color="primary" class="check-button">
                        Check Availability
                    </button>
                </div>
            </form>

            @if (availabilityChecked || datesModifiedAfterCheck) {
            <div class="availability-result">
                @if (datesModifiedAfterCheck) {
                <div class="warning-result">
                    <mat-icon color="accent">warning</mat-icon>
                    <div class="result-text">
                        <h4>Dates Changed</h4>
                        <p>You've modified the dates since your last availability check. Please click "Check
                            Availability" again before confirming.</p>
                    </div>
                </div>
                } @else if (availabilityFeasible) {
                <div class="feasible-result">
                    <mat-icon color="primary">check_circle</mat-icon>
                    <div class="result-text">
                        <h4>Feasible</h4>
                        <p>The visit can be scheduled within the selected date range.</p>
                    </div>
                </div>
                <div class="confirmation-actions">
                    <button (click)="confirmBooking()" mat-raised-button color="accent">
                        Confirm Booking
                    </button>
                </div>
                } @else {
                <div class="not-feasible-result">
                    <mat-icon color="warn">cancel</mat-icon>
                    <div class="result-text">
                        <h4>Not Feasible</h4>
                        <p>The visit cannot be scheduled within the selected date range.</p>
                        <p>Please select a different date range or contact the planning team.</p>
                    </div>
                </div>
                }
            </div>
            }
        </div>
        @if (!exitOnCompletion || allowResourceLocking) {
        <div class="booking-right-sidebar">
            @if (visit) {
            <mat-list>
                <h3 mat-subheader>Schedules</h3>
                @for (schedule of visit.visitScheduleList; track schedule) {
                <mat-list-item>
                    <mat-icon fontIcon="fa-wrench" fontSet="far" mat-list-icon></mat-icon>
                    <h4 [matTooltip]="schedule.sorDescription" mat-line>{{ schedule.quantity }}
                        x {{ schedule.sorDescription }}</h4>
                    <p mat-line>SMV: {{ schedule.smv }} minutes Code: {{ schedule.sorCode }}</p>
                </mat-list-item>
                }
                @if (!visit.visitScheduleList || visit.visitScheduleList.length === 0) {
                <mat-list-item>No schedules
                </mat-list-item>
                }
            </mat-list>
            }
            @if (allowResourceLocking) {
            <div class="resource-list">
                <div class="resource-list-header">
                    {{ resourceList?.length }} Resource{{ resourceList?.length > 1 ? 's' : '' }} Required
                </div>
                @for (resource of resourceList; track resource) {
                <div class="resource-list-item">
                    <div class="resource-list-item-header">
                        @if (this.resourceFormControl[resource.ticket.id]?.value?.imageUrl) {
                        <img [src]="this.resourceFormControl[resource.ticket.id].value.imageUrl" alt="User Avatar">
                        }
                        <h3>
                            @if (this.resourceFormControl[resource.ticket.id].value) {
                            {{ this.resourceFormControl[resource.ticket.id].value.name }}
                            }
                            @if (!this.resourceFormControl[resource.ticket.id].value) {
                            Not locked
                            }
                        </h3>
                    </div>
                    <div class="resource-list-item-content">
                        Skill Profile Required: {{ resource.ticket.skillProfile.description }}
                    </div>
                    <div class="resource-list-item-actions">
                        <button (click)="selectResource(resource.ticket)" color="primary" mat-flat-button>Choose
                            Resource</button>
                        @if (resourceFormControl[resource.ticket.id].value !== null) {
                        <button (click)="clearSelection(resource.ticket)" color="warn" mat-flat-button>
                            Unlock
                        </button>
                        }
                    </div>
                </div>
                }
            </div>
            }
        </div>
        }
    </div>
</div>
}